import { classnames } from 'tailwindcss-classnames';
import { Components } from '.generated/templates/Feature.Components.model';
import { Data } from '.generated/templates/Feature.Data.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { LayoutPageSectionShell } from 'components/authorable/layout/LayoutPageSection/LayoutPageSection';
import { LayoutRightThirdShell } from 'components/authorable/layout/LayoutRightThird/LayoutRightThird';
import DateRangeFacet from 'components/helpers/AlgoliaSearch/Facets/DateRangeFacet';
import SearchHit from 'components/helpers/AlgoliaSearch/SearchHit';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import PlaceholderWrapper from 'components/helpers/PlaceholderWrapper/PlaceholderWrapper';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { Configure, Hits } from 'react-instantsearch-hooks-web';
import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';
import Container from 'components/helpers/Container';
import { HitProps } from 'components/helpers/AlgoliaSearch/Hits/_types';
import { useCallback, useState } from 'react';
import ClearFacets from 'components/helpers/AlgoliaSearch/Facets/ClearFacets';
import EventListingCategoryFacet from 'components/helpers/AlgoliaSearch/Facets/EventListingCategoryFacet';
import { CustomPagination } from 'components/helpers/AlgoliaSearch/CustomPagination';
import { getEnumValue } from 'lib/util/enum';
import { defaultFilter } from 'components/helpers/AlgoliaSearch/util';
import SearchSort from 'components/helpers/AlgoliaSearch/SearchSort';

export type EventListingProps = Sitecore.Override.ComponentBase &
  Components.NewsAndEvents.Fields.EventListing & {
    fields?: {
      categoryFilter: Data.Events.Fields.EventCategory[];
    };
  };

type EventListingWrapperProps = EventListingProps & {
  desktopFacets: JSX.Element;
  mobileFacets: JSX.Element;
  hits: JSX.Element;
  configSearch: JSX.Element;
};

const EventListingSearchHit = ({ hit }: HitProps) => {
  return <SearchHit hit={hit} component="eventListing" />;
};

const EventListingWrapper = ({
  rendering,
  fields,
  desktopFacets,
  mobileFacets,
  hits,
  configSearch,
}: EventListingWrapperProps): JSX.Element => {
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  const itemsCenterCss = `items-center`;
  return (
    <div data-component="authorable/news-and-events/eventlisting">
      <Container size="standard" className={classnames('bg-white', 'md:bg-gray-quartz')}>
        <div className={classnames('flex', 'flex-col', 'md:flex-row', itemsCenterCss, 'p-6')}>
          <div className={classnames('hidden', 'md:flex', itemsCenterCss)}>
            <h3 className={classnames('text-blue-navy', 'heading-md', 'mr-6')}>
              <SvgIcon
                icon="filter"
                size="xs"
                viewBoxSize="0 0 24 24"
                className={classnames('mr-2', '-mt-1')}
              />
              Filter
            </h3>
            {desktopFacets}
          </div>
          <div className={classnames('md:hidden', 'block', 'w-full')}>
            <details
              className={classnames(
                'relative',
                'mr-20',
                'bg-white',
                'border',
                'border-gray-spunPearl',
                'rounded-md',
                'p-6',
                'w-full'
              )}
            >
              <summary
                className={classnames(
                  'relative',
                  'w-full',
                  'flex',
                  itemsCenterCss,
                  'justify-between'
                )}
              >
                <div
                  className={classnames(
                    'text-blue-navy',
                    'heading-md',
                    'w-full',
                    'flex',
                    itemsCenterCss,
                    'justify-between'
                  )}
                >
                  <span className={classnames('relative')}>
                    <SvgIcon
                      icon="filter"
                      size="xs"
                      viewBoxSize="0 0 24 24"
                      className={classnames('mr-2', '-mt-1')}
                    />
                    Filter
                  </span>
                  <span>
                    <SvgIcon
                      icon="chevron-down"
                      size="xs"
                      viewBoxSize="0 0 24 24"
                      className={classnames('mr-2')}
                    />
                  </span>
                </div>
              </summary>
              <div className={classnames('w-full')}>{mobileFacets}</div>
            </details>
          </div>
        </div>
      </Container>
      <LayoutPageSectionShell theme="theme-whisper">
        {configSearch}
        <LayoutRightThirdShell
          left={hits}
          right={<PlaceholderWrapper name="nmdp-one-third-right" rendering={rendering} />}
        />
      </LayoutPageSectionShell>
    </div>
  );
};

const EventListingWithSearch = ({
  fields,
  ...eventListingProps
}: EventListingProps): JSX.Element => {
  const listed =
    parseInt(getEnumValue<string>(fields?.listingsPerPage), 10) == null ||
    Number.isNaN(parseInt(getEnumValue<string>(fields?.listingsPerPage), 10))
      ? 10
      : parseInt(getEnumValue<string>(fields?.listingsPerPage), 10);

  const [eventsPerPage, setEventsPerPage] = useState(listed);

  const handleTypeSelect = useCallback(
    (count: string) => {
      const selectedEventsPerPage = parseInt(count, 10);
      setEventsPerPage(selectedEventsPerPage);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setEventsPerPage]
  );

  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }

  const eventCategoryTitles = fields.categoryFilter?.map(
    (item: Data.Events.Fields.EventCategory) => {
      return item.fields?.eventCategoryTitle.value || '';
    }
  );
  const todayDate = new Date();
  todayDate.setHours(24, 0, 0, 0);
  const dateFilter = defaultFilter();

  let configureFilters = `type:event AND (${dateFilter})`;

  if (eventCategoryTitles.length) {
    configureFilters = configureFilters.concat(' AND (');
    eventCategoryTitles.forEach(function BuildEventCategoryFilter(value, index) {
      if (value) {
        configureFilters =
          index === 0
            ? configureFilters.concat('eventCategory:').concat(`"${value}"`)
            : configureFilters.concat(' OR eventCategory:').concat(`"${value}"`);
      }
    });
    configureFilters = configureFilters.concat(')');
  }

  return (
    <>
      <EventListingWrapper
        fields={fields}
        configSearch={
          <>
            <Configure analytics={false} filters={configureFilters} hitsPerPage={eventsPerPage} />
            <SearchSort profile="event-hidden-date-asc" />
          </>
        }
        desktopFacets={
          <>
            <EventListingCategoryFacet
              eventCategoryTitles={eventCategoryTitles}
              screenSize="desktop"
            />
            <DateRangeFacet
              label=""
              dateField="startDate"
              startFieldName="eventStartDate"
              endFieldName="eventEndDate"
              horizontal
              disallowDateRange
            />

            <ClearFacets />
            <span
              className={classnames('heading-2xs', 'text-gray-brightGray', 'font-semibold', 'mx-2')}
            >
              Events per Page
            </span>
            <span>
              <select
                aria-label="Events per Page"
                className={classnames(
                  'border',
                  'border-gray-spunPearl',
                  'rounded-md',
                  'p-2',
                  'mx-2'
                )}
                value={eventsPerPage}
                onChange={(e) => handleTypeSelect(e.currentTarget.value)}
              >
                <option key={10} value={10}>
                  10
                </option>
                <option key={20} value={20}>
                  25
                </option>
                <option key={50} value={50}>
                  50
                </option>
                <option key={100} value={100}>
                  100
                </option>
              </select>
            </span>
          </>
        }
        mobileFacets={
          <>
            <EventListingCategoryFacet
              eventCategoryTitles={eventCategoryTitles}
              screenSize="mobile"
            />
            <hr className={classnames('my-6')} />
            <DateRangeFacet
              label="Date"
              dateField="startDate"
              startFieldName="eventStartDate"
              endFieldName="eventEndDate"
              disallowDateRange
            />
            <ClearFacets />
          </>
        }
        hits={
          <>
            <Hits hitComponent={EventListingSearchHit} />
            <div>
              <CustomPagination padding={1} />
            </div>
          </>
        }
        {...eventListingProps}
      />
    </>
  );
};

const FakeFacets = (): JSX.Element => {
  return (
    <>
      <h5>Search Facets</h5>
    </>
  );
};
const FakeHits = (): JSX.Element => {
  return (
    <>
      <h5>Events will show here</h5>
    </>
  );
};

const EventListing = (props: EventListingProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const pageEditing = sitecoreContext.pageEditing === true;
  if (pageEditing) {
    return (
      <EventListingWrapper
        configSearch={<></>}
        desktopFacets={<FakeFacets />}
        mobileFacets={<FakeFacets />}
        hits={<FakeHits />}
        {...props}
      />
    );
  }
  return <EventListingWithSearch {...props} />;
};

export default EventListing;
